import { useMemo } from 'react';
import { useFetchDatasetsQuery } from '@/features/datasetSlice';
import type {
  RasterLayerSource as ApiRasterLayerSource,
  VectorLayerSource as ApiVectorLayerSource,
} from '@/generated/types/navigator/types.gen';

/** A mosaic layer, whose tiles the tiler serves for the release it belongs to. */
export interface RasterLayerSource {
  kind: 'raster';
  layerType: string;
  name: string;
  description: string | null;
  releaseId: string;
}

/** A table-backed layer, drawn from navigator's MVT tiles. */
export interface VectorLayerSource {
  kind: 'vector';
  layerType: string;
  name: string;
}

export type LayerSource = RasterLayerSource | VectorLayerSource;

const toLayerSource = (
  source: ApiRasterLayerSource | ApiVectorLayerSource
): LayerSource =>
  source.kind === 'raster'
    ? {
        kind: 'raster',
        layerType: source.layer_type,
        name: source.name,
        description: source.description ?? null,
        releaseId: source.release_id,
      }
    : { kind: 'vector', layerType: source.layer_type, name: source.name };

/**
 * Loads the layers available to the current client from `/v1/dataset`. The
 * request is client- and scan-scoped via the auth headers, and its cache is
 * cleared on client/scan switches by `resetClientScopedApiState`.
 */
export const useDatasets = () => {
  const { data, isLoading, isFetching, isSuccess, isError } =
    useFetchDatasetsQuery();

  const datasets = useMemo(() => (data ?? []).map(toLayerSource), [data]);

  return { datasets, isLoading, isFetching, isSuccess, isError };
};
